import loginStyle from "../../../../../../assets/scss/common/modal/loginModal.module.scss";
import memberStyle from "../../../../../../assets/scss/sub/editMember.module.scss";
import React, {RefObject, useEffect} from "react";
import {useAppDispatch, useAppSelector, useLanguage} from "../../../../../../hooks/hooks";
import {countryList, getCountriesAsync, setCountries} from "../../../../../../store/country/countrySlice";
import {Country} from "../../../../../../types/country";
import {filterAllowedCountries, handleOnlyNumberChange} from "../../../../../../util/commonFunctions";
import {ValidateStatus} from "../hooks/useSignupValidator";
import {StyleRequestType} from "../../../../../../types/common";
import {useTranslation} from "react-i18next";

interface Props {
    phoneCode: string;
    setPhoneCode: (_: string) => void;
    phoneNumber: string;
    setPhoneNumber: (_: string) => void;
    showCellphone: boolean;
    setShowCellphone: (_: boolean) => void;
    phoneCodeValidate: ValidateStatus;
    setPhoneCodeValidate: (_: ValidateStatus) => void;
    phoneNumberValidate: ValidateStatus;
    setPhoneNumberValidate: (_: ValidateStatus) => void;
    styleRequestType: StyleRequestType,
    phoneRef?: RefObject<HTMLInputElement>;
}

export function SignupCellphoneField({
     phoneCode,
     setPhoneCode,
     phoneNumber,
     setPhoneNumber,
     showCellphone,
     setShowCellphone,
     phoneCodeValidate,
     setPhoneCodeValidate,
    phoneNumberValidate,
    setPhoneNumberValidate,
    styleRequestType,
    phoneRef
}: Props) {
    const { t } = useTranslation("userInfo");
    const dispatch = useAppDispatch();
    const { isKorean } = useLanguage();
    const countries = useAppSelector(countryList);
    const styles = styleRequestType === StyleRequestType.SIGN_UP ? loginStyle :  memberStyle;

    const allowedCountryCodes = ['KR', 'US', 'MN', 'TW', 'VN'];
    const filteredOptions = filterAllowedCountries({countries, allowedKeys:allowedCountryCodes});

    // 현재 선택된 국가
    const selectedCountry = countries?.find(option => option.codePhone[0] === phoneCode);
    const isDefaultOption = !selectedCountry;

    // 국가정보 조회 함수
    useEffect(() => {
        if (!countries) {
            dispatch(getCountriesAsync())
                .unwrap()
                .then((countries) => {
                    dispatch(setCountries(countries));
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countries]);

    // 휴대전화 국가 셀렉트 바 선택 함수
    const handleCellphoneClick = (option: Country) => {
        setPhoneCode(option.codePhone[0]);
        setShowCellphone(false);
        setPhoneCodeValidate(ValidateStatus.NONE);
    };

    const getValidationMessage = () => {
        // 국가 선택 케이스
        if (phoneCodeValidate === ValidateStatus.UNFILLED) {
            return <span className={styles.validateText}>{t('edit.warning.cellphone.nationalityUnfilled')}</span>
        }

        // 휴대폰 번호 미작성 케이스
        if (phoneCodeValidate === ValidateStatus.NONE) {
            if (phoneNumberValidate === ValidateStatus.UNFILLED) {
                return <span className={styles.validateText}>{t('edit.warning.cellphone.phoneNumberUnfilled')}</span>
            }

            // 국내 휴대폰 번호 길이 제한 케이스
            if (phoneCode === '+82') {
                if (phoneNumberValidate === ValidateStatus.BELOW_REQUIRED_LENGTH || phoneNumberValidate === ValidateStatus.OVER_REQUIRED_LENGTH) {
                    return <span className={styles.validateText}>{t('edit.warning.cellphone.belowLength')}</span>;
                }
            // 해외 휴대폰 번호 길이 제한 케이스
            } else {
                if (phoneNumberValidate === ValidateStatus.BELOW_REQUIRED_LENGTH_FOREIGN) {
                    return <span className={styles.validateText}>{t('edit.warning.cellphone.belowLengthForeign')}</span>;
                }
            }
        }

        return null;
    };

    return (
        <div className={styles.selectWrapper}>
            <div className={styles.selectTitleWrapper}>
                <span className={styles.highLight}>{t('form.required')}</span>
                <span className={styles.titleText}>{t('form.cellphone.label')}</span>
            </div>
            <div className={styles.selectTextFieldWrapper} onClick={() => setShowCellphone(!showCellphone)}>
          <span className={`${styles.selectText} ${isDefaultOption ? styles.defaultText : ''}`}>
          {filteredOptions ? (
              selectedCountry ?
                  `(${selectedCountry.codePhone}) ${isKorean ? selectedCountry.nameKor : selectedCountry.nameEn}`
                  : '국가 선택'
          ) : 'Loading...'}
        </span>
                <div className={styles.arrowImg}></div>
                {filteredOptions && showCellphone && (
                    <div className={`${styles.optionStyle} ${styles.cellPhoneSelectStyle} `}>
                        {filteredOptions.map((option) => (
                            <button
                                key={isKorean ? option.nameKor : option.nameEn}
                                className={`${styles.optionTextWrapper} ${option.codePhone[0] === phoneCode ? styles.selectedOption : ''}`}
                                onClick={() => handleCellphoneClick(option)}
                            >
                                <span
                                    className={`${styles.optionText} ${option.codePhone[0] === phoneCode ? styles.selectedOption : ''}`}>
                                    ({option.codePhone[0]}) {isKorean ? option.nameKor : option.nameEn}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
            <div className={styles.selectTextFieldWrapper}>
                <input className={styles.selectText} ref={phoneRef}
                       maxLength={phoneCode === '+82' ? 11 : 15}
                       value={phoneNumber}
                       onChange={e => {
                           handleOnlyNumberChange(e, setPhoneNumber)
                           setPhoneNumberValidate(ValidateStatus.NONE)
                       }}
                       placeholder={t('form.cellphone.placeholder')}/>
            </div>
            {getValidationMessage()}
            <span className={styles.cellPhoneInfo}>{t('form.cellphone.desc1')}
                <br/>{t('form.cellphone.desc2')}
            </span>
        </div>
    )
}